import styled from '@emotion/styled';
import * as tokens from '@tokens';
import { Breadcrumbs } from '@components/Breadcrumbs/Breadcrumbs';
import { Text } from '@components/Text/Text';
import type { ReactNode } from 'react';
import type { BreadcrumbsProps } from '@components/Breadcrumbs/Breadcrumbs';

export interface PageHeaderProps {
  /**
   * Header text for giving context to the content on the page.
   */
  title: string;
  /**
   * Subheader text for giving some extra context.
   */
  subtitle?: ReactNode | string;
  /**
   * A list of elements for displaying primary and secondary actions. Try to limit the amount to 3.
   * When using the Icon buttons, make sure to follow the guidelines defined by the [Action menu](https://zeroheight.com/76c439891/v/latest/p/24c6f1-action-menu)
   */
  actions?: ReactNode;
  /**
   * A list of `Breadcrumbs.Item` components for displaying the page's hierarchy.
   */
  breadcrumbItems?: BreadcrumbsProps['children'];
  /**
   * Styles override.
   */
  className?: string;
}

const S = {
  ContentContainer: styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
  `,
  TitleWrapper: styled.div`
    margin: ${tokens.spacer.x1} ${tokens.spacer.x3} ${tokens.spacer.x1} 0;
  `,
  ActionsContainer: styled.div`
    ${tokens.flexContainer('default', { gap: tokens.spacer.x1 })}
    align-items: center;
    justify-content: flex-end;
  `,
};

/**
 * A header with common actions and design elements built-in.
 *
 * {@link https://zeroheight.com/76c439891/v/latest/p/9165ad-page-header Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-pageheader--page-header Code documentation}
 *
 * @example
 * <PageHeader title="Page Title" subtitle="Optional Subtitle" />
 *
 */
export const PageHeader = ({
  title,
  actions,
  breadcrumbItems,
  subtitle,
  className,
}: PageHeaderProps): JSX.Element => {
  return (
    <div className={className}>
      {breadcrumbItems && <Breadcrumbs>{breadcrumbItems}</Breadcrumbs>}
      <S.ContentContainer>
        <S.TitleWrapper>
          <Text as="h1" textType="header3">
            {title}
          </Text>
        </S.TitleWrapper>
        {actions && <S.ActionsContainer>{actions}</S.ActionsContainer>}
      </S.ContentContainer>
      {subtitle && (
        <Text as="span" textType="body2" textColor="text_light">
          {subtitle}
        </Text>
      )}
    </div>
  );
};

PageHeader.displayName = 'PageHeader';
